import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ArrowUpDown, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange, loadsForUnit } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

// Unit-centric mileage -- distinct from the driver-centric Miles Report.
// Fleet managers use this for maintenance/depreciation scheduling per truck,
// which needs total miles per unit regardless of driver reassignment
// mid-period (the driver-centric report would split that across drivers).
export default function UnitMilesReport() {
  const [loads, setLoads] = useState([]);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('Total Miles');
  const [sortDir, setSortDir] = useState(-1);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-delivery_date', 500),
      client.entities.Asset.filter({ asset_type: 'Truck' }, 'unit_number', 200),
      client.entities.DriverProfile.list('full_name', 100),
    ]).then(([l, a, d]) => { setLoads(l); setAssets(a); setDrivers(d); setLoading(false); });
  }, []);

  const filtered = loads.filter(l => inRange(l.delivery_date || l.pickup_date, start, end) && l.assigned_driver_email);

  let rows = assets.filter(a => a.assigned_driver_email).map(a => {
    const unitLoads = loadsForUnit(filtered, a);
    const driver = drivers.find(d => d.user_email === a.assigned_driver_email);
    const loaded = unitLoads.reduce((s, l) => s + (l.miles || 0), 0);
    const deadhead = unitLoads.reduce((s, l) => s + (l.deadhead_miles || 0), 0);
    return {
      'Unit #': a.unit_number,
      Driver: driver?.full_name || a.assigned_driver_email || '—',
      Loads: unitLoads.length,
      'Loaded Miles': Math.round(loaded),
      'Deadhead Miles': Math.round(deadhead),
      'Total Miles': Math.round(loaded + deadhead),
    };
  }).filter(r => r.Loads > 0);

  rows.sort((a, b) => sortDir * ((b[sortKey] ?? 0) - (a[sortKey] ?? 0)));

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const grandTotal = rows.reduce((s, r) => s + r['Total Miles'], 0);

  const csvRows = rows;

  const handlePrint = () => {
    const cols = ['Unit #', 'Driver', 'Loads', 'Loaded Miles', 'Deadhead Miles', 'Total Miles'];
    const dataRows = rows.map(r => [
      { value: r['Unit #'], class: 'bold' }, r.Driver, r.Loads,
      r['Loaded Miles'].toLocaleString(), r['Deadhead Miles'].toLocaleString(),
      { value: r['Total Miles'].toLocaleString(), class: 'gold' },
    ]);
    const totalsRow = ['TOTALS', '', rows.reduce((s, r) => s + r.Loads, 0),
      rows.reduce((s, r) => s + r['Loaded Miles'], 0).toLocaleString(),
      rows.reduce((s, r) => s + r['Deadhead Miles'], 0).toLocaleString(),
      { value: grandTotal.toLocaleString(), class: 'gold' }];
    const cards = [
      { label: 'Active Units', value: rows.length, color: 'gold' },
      { label: 'Total Miles', value: grandTotal.toLocaleString(), color: 'positive' },
    ];
    const html = generatePrintHTML('Unit Miles Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'unit-miles.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Unit #', 'Driver', 'Loads', 'Loaded Miles', 'Deadhead Miles', 'Total Miles'].map(h => (
                <th key={h} onClick={() => toggleSort(h)} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap cursor-pointer hover:text-foreground select-none">
                  <span className="flex items-center gap-1">{h} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r['Unit #']} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-primary">{r['Unit #']}</td>
                <td className="px-3 py-2.5 text-foreground">{r.Driver}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.Loads}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Loaded Miles'].toLocaleString()}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Deadhead Miles'].toLocaleString()}</td>
                <td className="px-3 py-2.5 font-bold text-foreground">{r['Total Miles'].toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={5} className="px-3 py-2.5 text-foreground">TOTAL</td>
                <td className="px-3 py-2.5 text-foreground">{grandTotal.toLocaleString()}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No unit mileage data. Make sure assets have assigned drivers.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}
